import { roles } from '~/enums'
import { useCurrentRoleStore } from '~/stores/currentRole'

export function getRoleLandingPath(role?: Role) {
  switch (role) {
    case roles.ADMIN:
      return '/dashboard/admin'
    case roles.TEACHER:
      return '/dashboard/teacher'
    case roles.STUDENT:
      return '/classes'
    default:
      return '/'
  }
}

export function useCompleteSignIn() {
  const user = useSupabaseUser()
  const currentRoleStore = useCurrentRoleStore()
  const { load: loadMemberships, isSystemAdmin } = useSchoolMemberships()

  const complete = async (
    options: {
      redirectTo?: string
      preferredRole?: Pick<SchoolRole, 'schoolId' | 'role'>
    } = {},
  ) => {
    if (!user.value) return false

    const memberships = await loadMemberships(true)
    if (isSystemAdmin.value) {
      const school =
        memberships.find(
          (membership) =>
            membership.schoolId === currentRoleStore.currentRole.schoolId,
        ) ?? memberships[0]
      if (school) currentRoleStore.setCurrentRole(school)
      await navigateTo(
        options.redirectTo ||
          (school ? '/dashboard/admin' : '/dashboard/proposals'),
      )
      return true
    }
    const savedRole = currentRoleStore.currentRole
    const preferredRole = options.preferredRole
    const activeRole =
      memberships.find(
        (membership) =>
          membership.schoolId === preferredRole?.schoolId &&
          membership.role === preferredRole?.role,
      ) ??
      memberships.find(
        (membership) =>
          membership.schoolId === savedRole?.schoolId &&
          membership.role === savedRole?.role,
      ) ??
      memberships[0]

    if (!activeRole) {
      try {
        await $fetch('/api/proposal-admin/access')
        await navigateTo('/dashboard/proposals')
        return true
      } catch {
        return false
      }
    }
    currentRoleStore.setCurrentRole(activeRole)

    try {
      const { data: classes } = await $fetch<{ data: SchoolClass[] }>(
        '/api/userClasses',
        { query: { userId: user.value.id } },
      )
      currentRoleStore.setCurrentGrade(classes?.[0]?.classYear || '')
    } catch {
      // Class context improves the landing page but must never block sign-in.
      currentRoleStore.setCurrentGrade('')
    }

    await navigateTo(options.redirectTo || getRoleLandingPath(activeRole.role))
    return true
  }

  return { complete }
}
